/**
 * useTagFilter - 标签筛选 Hook
 *
 * 负责从物料列表中提取标签选项、管理选中状态、过滤数据。
 * 可复用于直播卡片和期权课堂卡片等带标签的物料。
 */

import { ref, computed, watch, type Ref, type ComputedRef } from 'vue-demi';
import type { MaterialSnapshotData } from '@ark/material-checkin-shared';
import type { LocalizedTag, LanguageCode } from '../../shared/types';

/**
 * 标签选项
 */
export interface TagOption {
  label: string;
  value: string;
}

/**
 * 带标签的卡片数据
 */
export interface TaggedCard {
  materialId: string;
  tags: string[];
}

/**
 * useTagFilter Hook 配置
 */
export interface UseTagFilterOptions {
  /** 物料数据列表 */
  materials: MaterialSnapshotData[] | Ref<MaterialSnapshotData[]>;
  /** 组件类型（用于过滤） */
  componentType: string;
  /** 当前语言 */
  lang: LanguageCode;
}

/**
 * useTagFilter Hook 返回值
 */
export interface UseTagFilterReturn {
  /** 当前选中的标签 */
  activeTag: Ref<string>;
  /** 标签选项列表 */
  tagOptions: ComputedRef<TagOption[]>;
  /** 当前生效的标签（如果没有选中则使用第一个） */
  currentTag: ComputedRef<string>;
  /** 根据标签过滤卡片 */
  filterByTag: <T extends TaggedCard>(cards: T[]) => T[];
  /** 初始化标签选择（首次加载时使用第一个标签） */
  initializeTag: () => void;
}

/**
 * 解包 Ref 或普通值
 */
function unref<T>(value: T | Ref<T>): T {
  return (value as Ref<T>)?.value !== undefined
    ? (value as Ref<T>).value
    : (value as T);
}

/**
 * 从物料列表中提取标签选项
 * 按 sortOrder 排序，去重
 */
export function extractTagOptions(
  materials: MaterialSnapshotData[],
  componentType: string,
  lang: LanguageCode,
): TagOption[] {
  const tagOrderMap = new Map<string, number>();

  for (const material of materials) {
    if (material.componentType !== componentType) continue;

    const tags = material.displayConfig.tags as LocalizedTag[] | undefined;
    if (!tags || tags.length === 0) continue;

    for (const tag of tags) {
      const tagText = tag.label?.[lang];
      if (!tagText) continue;

      // 记录标签的排序值（取最小的 sortOrder）
      const existingOrder = tagOrderMap.get(tagText);
      if (existingOrder === undefined || tag.sortOrder < existingOrder) {
        tagOrderMap.set(tagText, tag.sortOrder);
      }
    }
  }

  // 按 sortOrder 排序并生成选项
  const sortedTags = Array.from(tagOrderMap.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([tag]) => tag);

  return sortedTags.map((tag) => ({
    label: tag,
    value: tag,
  }));
}

/**
 * 标签筛选 Hook
 *
 * @example
 * ```typescript
 * // 直播卡片标签筛选
 * const {
 *   activeTag: activeLiveTag,
 *   tagOptions: liveTagOptions,
 *   filterByTag: filterLiveCards,
 *   initializeTag: initializeLiveTag,
 * } = useTagFilter({
 *   materials,
 *   componentType: 'live_banner_card',
 *   lang,
 * });
 *
 * const filteredLiveCards = computed(() => filterLiveCards(liveBannerCards.value));
 *
 * // 期权课堂标签筛选
 * const {
 *   activeTag: activeOptionTag,
 *   tagOptions: optionTagOptions,
 *   filterByTag: filterOptionCards,
 * } = useTagFilter({
 *   materials,
 *   componentType: 'option_classroom_card',
 *   lang,
 * });
 * ```
 */
export function useTagFilter(options: UseTagFilterOptions): UseTagFilterReturn {
  const { materials, componentType, lang } = options;

  const activeTag = ref('');
  const initialized = ref(false);

  /**
   * 标签选项列表
   */
  const tagOptions = computed<TagOption[]>(() => {
    const materialList = unref(materials);
    if (!materialList || materialList.length === 0) return [];
    return extractTagOptions(materialList, componentType, lang);
  });

  /**
   * 当前生效的标签
   */
  const currentTag = computed(() => {
    return activeTag.value || tagOptions.value[0]?.value || '';
  });

  /**
   * 初始化标签选择
   */
  function initializeTag(): void {
    if (!initialized.value && tagOptions.value.length > 0) {
      activeTag.value = tagOptions.value[0].value;
      initialized.value = true;
      console.log(
        `[useTagFilter] 初始化 ${componentType} 标签:`,
        activeTag.value,
      );
    }
  }

  /**
   * 监听标签选项变化，自动初始化
   */
  watch(
    () => tagOptions.value.length,
    (length) => {
      if (length > 0 && !initialized.value) {
        initializeTag();
      }
    },
    { immediate: true },
  );

  /**
   * 根据标签过滤卡片
   */
  function filterByTag<T extends TaggedCard>(cards: T[]): T[] {
    const tag = currentTag.value;
    if (!tag) return cards;
    return cards.filter((card) => card.tags.includes(tag));
  }

  return {
    activeTag,
    tagOptions,
    currentTag,
    filterByTag,
    initializeTag,
  };
}

export default useTagFilter;
